/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToCartBehavior } from '@msdyn365-commerce/components';
import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { ICartViewProps, IFlyoutCartLineItemViewProps } from '@msdyn365-commerce-modules/cart';
import { ICartIconViewProps } from '../../../modules/ext-cart-icon/ext-cart-icon';
import { Button, getPayloadObject, INodeProps, KeyCodes, Modal, Node, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';

import { IExtCartIconProps } from '../definition-extensions/ext-cart-icon.ext.props.autogenerated';
import { useCartTracker } from '../../../integrations/relewise/hooks/useCartTracker';

/**
 * ICartIconViewState: Interface for Cart Icon View State.
 */
interface ICartIconViewState {
    isModalOpen: boolean;
    prevIsProductAddedToCart: boolean;
    prevCartCount: number;
}

/**
 * Render Cart lines.
 * @param cartLines - Flyout cart line view props.
 * @param props - Cart icon view props.-
 * @returns JSX Element.
 */
const renderCartlines = (cartLines: IFlyoutCartLineItemViewProps[] | undefined, props: ICartIconViewProps): JSX.Element[] | null => {
    if (!cartLines) {
        //props.context.telemetry.error('Cartlines content is empty, module wont render');
        return null;
    }

    return cartLines.map((cartLine, index) => {
        const cartLineIndex = index;

        return (
            <Node {...props.miniCartItemWrapper} key={cartLineIndex}>
                <Node className={classnames(cartLine.storeLocation ? 'msc-cart-line__set-net-price' : '')}>{cartLine.cartline}</Node>
                {cartLine.storeLocation}
                {cartLine.remove}
            </Node>
        );
    });
};

/**
 * Initiate total price.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
const renderTotalPrice = (props: ICartIconViewProps & IExtCartIconProps<{}>): JSX.Element | null => {
    const cart = props.data.cart.result ?? undefined;
    const price = cart && !cart.hasInvoiceLine && (cart.cart.TotalAmount || undefined);
    const channelConfig = props.context.request.channel;
    const currencyCode = channelConfig?.Currency;
    const totalPrice = price
        ? props.context.cultureFormatter.formatCurrency(price)
        : props.context.cultureFormatter.formatCurrency(0, currencyCode);
    const totalPriceLabel = (props.config.totalPriceFormatString ?? props.resources.totalPriceFormatString).replace('{price}', '').trim();

    return cart && !cart.hasInvoiceLine ? (
        <div className='ms-cart-icon__subTotalText'>
            <span className='ms-cart-icon__subTotalPriceText'>
                {totalPriceLabel}
                <span className='ms-cart-icon__subTotalPrice'>{totalPrice}</span>
            </span>
        </div>
    ) : (
        <></>
    );
};

/**
 *
 * CartIconView component.
 * @extends {React.PureComponent<ICartIconViewProps>}
 */
export class CartIconView extends React.PureComponent<ICartViewProps & ICartIconViewProps & IExtCartIconProps<{}>, ICartIconViewState> {
    private readonly cartIconRef: React.RefObject<HTMLButtonElement> = React.createRef();

    public constructor(props: ICartViewProps & ICartIconViewState & ICartIconViewProps & IExtCartIconProps<{}>) {
        super(props);
        this.state = {
            isModalOpen: false,
            prevIsProductAddedToCart: false,
            prevCartCount: 0
        };
    }

    public static getDerivedStateFromProps(
        props: ICartViewProps & ICartIconViewProps & IExtCartIconProps<{}>,
        state: ICartIconViewState
    ): Partial<ICartIconViewState> | null {
        const isAdded = props.data.cart.result?.isProductAddedToCart ?? false;
        const currCount = props.data.cart.result?.totalItemsInCart ?? 0;
        const isMiniCartBehavior = props.context.app.config.addToCartBehavior === AddToCartBehavior.showMiniCart;

        const flagJustBecameTrue = isAdded && !state.prevIsProductAddedToCart;
        const countIncreasedWhileFlagStaysTrue =
            isAdded && state.prevIsProductAddedToCart && !state.isModalOpen && currCount > state.prevCartCount;

        if (isMiniCartBehavior && (flagJustBecameTrue || countIncreasedWhileFlagStaysTrue)) {
            return { isModalOpen: true, prevIsProductAddedToCart: true, prevCartCount: currCount };
        }

        if (currCount !== state.prevCartCount) {
            return { prevCartCount: currCount };
        }

        if (!isAdded && state.prevIsProductAddedToCart) {
            return { prevIsProductAddedToCart: false };
        }

        return null;
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            window.addEventListener('keydown', this._escFunction, false);
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('keydown', this._escFunction, false);
        }
    }

    /**
     * Render Cart Item count.
     * @returns JSX Element.
     */
    public render(): JSX.Element | null {
        return (
            <Node {...this.props.miniCartWrapper} {...this.props.renderModuleAttributes(this.props)}>
                <button onClick={this._openModal} className='ms-cart-icon-wrapper msc-btn' ref={this.cartIconRef}>
                    {this.props.cartIcon}
                </button>
                <Node {...this._modalContainer()}>
                    <Node className='ms-cart-icon__header msc-modal__header'>
                        {this.props.flyoutTitle}
                        {this._renderCartItemCount(this.props)}
                        {this._renderCartCloseIcon()}
                    </Node>
                    <Node className='ms-cart-icon__body msc-modal__body'>
                        {this.props.freeDeliveryCountdown}
                        {this.props.data.cart.result?.isEmpty ? this.props.slots.emptyCart : null}
                        {this.props.checkoutBlockedDueToUnavailableFunds}
                        <Node {...this.props.CartlinesWrapper}>{renderCartlines(this.props.cartlines, this.props)}</Node>
                    </Node>
                    <Node className='ms-cart-icon__footer msc-modal__footer'>
                        {this.props.slots.promoContentItem}
                        {/** BS CUSTOM - popupContent */}
                        {this.props.popupContent}
                        {renderTotalPrice(this.props)}
                        {this.props.subTotalMessage}
                        {this.props.checkoutAsSignInUserButton}
                        {this.props.checkoutAsGuestButton}
                        {this.props.data.cart.result?.isEmpty ? null : this.props.goToCartButton}
                        <Node className='ms-cart-icon__btn-section'>
                            {this.props.data.cart.result?.isEmpty ? (
                                <Button
                                    className='ms-cart-icon__btn-backtoshopping'
                                    href={getUrlSync('home', this.props.context.actionContext)}
                                >
                                    {this.props.resources.continueShoppingButtonTitle}
                                </Button>
                            ) : null}
                        </Node>
                        <Node className='ms-cart-icon__payment-options-container'>{this.props.paymentOptionsImgs}</Node>
                    </Node>
                </Node>
            </Node>
        );
    }

    /**
     * Initiate modal container.
     * @returns Inode props.
     */
    private readonly _modalContainer = (): INodeProps => {
        return {
            tag: Modal,
            placement: 'bottom-end',
            hideArrow: true,
            className: 'ms-cart-icon__minicartmodal-container',
            wrapClassName: 'ms-cart-icon__minicartmodal',
            isOpen: this.state.isModalOpen,
            toggle: this.closeModal
        };
    };

    /**
     * Initiate open modal.
     */
    private readonly _openModal = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();
        const payLoad = getPayloadObject('click', this.props.telemetryContent!, 'cart-icon', '');
        onTelemetryClick(this.props.telemetryContent!, payLoad, 'cart-icon');
        this.setState({
            isModalOpen: true
        });
    };

    /**
     * Initiate close modal.
     */
    private readonly closeModal = (): void => {
        this.cartIconRef.current?.focus();
        this.setState({
            isModalOpen: false
        });
    };

    /**
     * Initiate cart close Icon.
     * @returns JSX Element.
     */
    private readonly _renderCartCloseIcon = (): JSX.Element | null => {
        return <Button className='ms-cart-icon__btn-close' aria-label='Close' onClick={this.closeModal} />;
    };

    /**
     * Render Cart Item count.
     * @param props - Cart icon view props.
     * @returns JSX Element.
     */
    private readonly _renderCartItemCount = (props: ICartIconViewProps & IExtCartIconProps<{}>): JSX.Element | null => {
        const defaultCartItemCount: number = 0;

        const cartItemlength = props.data.cart.result ? props.data.cart.result.totalItemsInCart : defaultCartItemCount;
        const cartItemCount = `${cartItemlength} ${cartItemlength === 1 ? props.resources.item : props.resources.items}`;

        return <Node className='ms-cart-icon__count'>{cartItemCount}</Node>;
    };

    /**
     * Handle escape click to close modal.
     * @param event - On press of any key.
     */
    private readonly _escFunction = (event: KeyboardEvent) => {
        if (event.keyCode === KeyCodes.Escape && this.state.isModalOpen) {
            this.closeModal();
        }
    };
}

const CartIconViewWithTrackingHook = (props: ICartViewProps & ICartIconViewProps & IExtCartIconProps<{}>) => {
    const cart = props.data.cart.result?.cart;
    useCartTracker(cart?.SubtotalAmount, props.cartlines, props.areCartLinesInitialized, props.context.request, props.context.telemetry);

    return <CartIconView {...props} />;
};

export default CartIconViewWithTrackingHook;
